import type { ReactNode } from 'react'
import { useSlidingThumb } from '@/lib/useSlidingThumb'
import { cn } from '@/lib/utils'

export interface Segment<T extends string> {
  value: T
  label: string
  count?: number
}

interface SegmentedControlProps<T extends string> {
  segments: Segment<T>[]
  value: T
  onChange: (value: T) => void
  className?: string
  /** Waar deze keuze over gaat. Zonder label is een losse knoppenrij voor een schermlezer betekenisloos. */
  label?: string
  /**
   * Een laatste slot dat geen gewone keuze is maar iets opent, bijvoorbeeld een
   * puntjes-knop met een langere lijst erachter. Moet `segmentClass()` dragen en
   * `data-active` zetten, anders vindt de thumb hem niet.
   */
  trailing?: ReactNode
}

/**
 * De vorm van een segment, zodat een trailing-slot er niet naast valt.
 */
export function segmentClass(active: boolean): string {
  return cn(
    'relative z-10 inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium transition-colors duration-150',
    active ? 'text-ink' : 'text-ink-soft hover:text-ink',
  )
}

export function SegmentedControl<T extends string>({
  segments,
  value,
  onChange,
  className,
  label,
  trailing,
}: SegmentedControlProps<T>) {
  const { containerRef, thumb } = useSlidingThumb(value)

  return (
    <div
      ref={containerRef}
      role={label ? 'group' : undefined}
      aria-label={label}
      className={cn(
        'scrollbar-none relative inline-flex max-w-full items-center gap-0.5 overflow-x-auto rounded-lg border border-hairline bg-fill-2 p-0.5',
        className,
      )}
    >
      <span
        aria-hidden
        className="pointer-events-none absolute bottom-0.5 top-0.5 rounded-md bg-surface shadow-card transition-[transform,width] duration-200 ease-spring"
        style={{
          width: thumb.width,
          transform: `translateX(${thumb.left - 2}px)`,
          opacity: thumb.ready ? 1 : 0,
        }}
      />
      {segments.map((segment) => {
        const active = segment.value === value
        return (
          <button
            key={segment.value}
            type="button"
            data-active={active}
            aria-pressed={active}
            onClick={() => onChange(segment.value)}
            className={segmentClass(active)}
          >
            {segment.label}
            {segment.count !== undefined ? (
              <span
                className={cn(
                  'num rounded-pill px-1.5 text-xs',
                  active ? 'bg-accent-600/10 text-accent-700' : 'bg-fill-2 text-ink-faint',
                )}
              >
                {segment.count}
              </span>
            ) : null}
          </button>
        )
      })}
      {trailing}
    </div>
  )
}
